/**
 * Página: mapear etapas Kommo a estados de reunión.
 * Independiente de Configuración → Etapas / Orígenes.
 * @module-isolated analytics-mapeo-reunion
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorState, PageHeader } from '../../../shared/components/index.js';
import { MEETING_STATUSES, emptyMeetingCounts, meetingStatusLabel } from '../../../shared/constants/meeting-statuses.js';
import { loadMeetingStages, persistMeetingStages } from '../services/meetingStagesService.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';

function sortStages(list) {
  return (list || []).slice().sort((a, b) => {
    const pa = String(a.pipelineName || a.pipelineId || '');
    const pb = String(b.pipelineName || b.pipelineId || '');
    if (pa !== pb) return pa.localeCompare(pb, 'es');
    return String(a.stageName || a.stageId || '').localeCompare(
      String(b.stageName || b.stageId || ''),
      'es'
    );
  });
}

function stageKey(it) {
  return String(it.pipelineId) + '::' + String(it.stageId);
}

function normalizeItem(it) {
  const status = String((it && it.meetingStatus) || '').trim();
  return {
    ...it,
    meetingStatus: status
  };
}

function countByStatus(items) {
  const counts = emptyMeetingCounts();
  (items || []).forEach((it) => {
    const s = String(it.meetingStatus || '');
    if (counts[s] != null) counts[s] += 1;
  });
  return counts;
}

function statusSummary(total, mapped, counts) {
  const parts = MEETING_STATUSES.map((s) => s.label + ' ' + (counts[s.id] || 0));
  return total + ' etapas Kommo · ' + mapped + ' mapeadas · ' + parts.join(' · ');
}

export default function MeetingStagesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState({ msg: '', level: '' });
  const [pipelineFilter, setPipelineFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bulkStatus, setBulkStatus] = useState('realized');

  const load = useCallback(async () => {
    setBusy('load');
    setError('');
    try {
      const res = await loadMeetingStages();
      if (!res || res.success === false) {
        throw new Error((res && res.error) || 'No se pudieron cargar las etapas.');
      }
      const next = sortStages((res.items || []).map(normalizeItem));
      setItems(next);
      setDirty(false);
      const counts = res.countsByStatus || countByStatus(next);
      const n = Number(res.markedCount || next.filter((it) => it.meetingStatus).length);
      setStatus({
        msg: statusSummary(res.total || next.length, n, counts),
        level: 'ok'
      });
    } catch (err) {
      setError((err && err.message) || 'Error al cargar.');
    } finally {
      setBusy('');
      setLoading(false);
    }
  }, []);

  useAnalyticsRefresh(load);

  useEffect(() => {
    load();
  }, [load]);

  const pipelines = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const id = String(it.pipelineId || '');
      if (!id || map.has(id)) return;
      map.set(id, { id: id, name: it.pipelineName || id });
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), 'es')
    );
  }, [items]);

  const counts = useMemo(() => countByStatus(items), [items]);
  const mappedCount = useMemo(
    () => items.filter((it) => it.meetingStatus).length,
    [items]
  );

  const visible = useMemo(() => {
    return items.filter((it) => {
      if (pipelineFilter && String(it.pipelineId) !== String(pipelineFilter)) return false;
      if (statusFilter === 'mapped' && !it.meetingStatus) return false;
      if (statusFilter === 'unmapped' && it.meetingStatus) return false;
      if (
        statusFilter !== 'all' &&
        statusFilter !== 'mapped' &&
        statusFilter !== 'unmapped' &&
        it.meetingStatus !== statusFilter
      ) {
        return false;
      }
      return true;
    });
  }, [items, pipelineFilter, statusFilter]);

  function setStatusFor(it, value) {
    const k = stageKey(it);
    setItems((prev) =>
      prev.map((row) => (stageKey(row) === k ? { ...row, meetingStatus: value } : row))
    );
    setDirty(true);
  }

  function assignVisible(value) {
    const keys = new Set(visible.map(stageKey));
    setItems((prev) =>
      prev.map((row) => (keys.has(stageKey(row)) ? { ...row, meetingStatus: value } : row))
    );
    setDirty(true);
  }

  async function runSave() {
    setBusy('save');
    setStatus({ msg: 'Guardando…', level: '' });
    try {
      const payload = items
        .filter((it) => it.meetingStatus)
        .map((it) => ({
          pipelineId: it.pipelineId,
          stageId: it.stageId,
          pipelineName: it.pipelineName || '',
          stageName: it.stageName || '',
          meetingStatus: it.meetingStatus
        }));
      const res = await persistMeetingStages(payload, {});
      if (!res || res.success === false) {
        throw new Error((res && res.error) || 'No se pudo guardar.');
      }
      setDirty(false);
      setStatus({
        msg: res.message || 'Guardado: ' + (res.saved || payload.length) + ' etapa(s).',
        level: 'ok'
      });
    } catch (err) {
      setStatus({
        msg: (err && err.message) || 'Error al guardar.',
        level: 'bad'
      });
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="analytics-module analytics-meeting-map-page">
      <PageHeader
        eyebrow="Configuración"
        title="Estados de reunión"
        subtitle="Asigná cada etapa del embudo Kommo a un estado de reunión. Sin agendar = todavía puede agendar. No agendará = no responde, no califica, se habló por WhatsApp/llamada u otro motivo y no va a agendar. Reserva o ganado → Realizada si la reunión ya ocurrió."
        actions={
          <div className="analytics-toolbar">
            <button
              type="button"
              className="analytics-btn analytics-btn-ghost"
              disabled={!!busy}
              onClick={() => load()}
            >
              Recargar
            </button>
            <button
              type="button"
              className="analytics-btn"
              disabled={!!busy || !dirty}
              onClick={runSave}
            >
              {busy === 'save' ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        }
      />

      <p className="analytics-cohort-note">
        Independiente de{' '}
        <Link to="/configuracion">Configuración → Etapas / Orígenes</Link>. Misma idea:
        vos definís el mapeo; la home de Leads cuenta sobre el estado actual.
      </p>

      {status.msg ? (
        <p
          className={
            'analytics-meta-bar' + (status.level === 'bad' ? ' analytics-cohort-warn' : '')
          }
        >
          {status.msg}
          {dirty ? ' · cambios sin guardar' : ''}
        </p>
      ) : null}

      <ErrorState message={error} onRetry={() => load()} />

      {loading ? <p className="analytics-empty">Cargando etapas de Kommo…</p> : null}

      {!loading ? (
        <>
          <div className="analytics-stage-toolbar">
            <label className="analytics-field" style={{ margin: 0 }}>
              <span>Embudo Kommo</span>
              <select
                value={pipelineFilter}
                onChange={(e) => setPipelineFilter(e.target.value)}
                disabled={!!busy}
              >
                <option value="">Todos</option>
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="analytics-field" style={{ margin: 0 }}>
              <span>Asignar visibles a</span>
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                disabled={!!busy}
              >
                {MEETING_STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="analytics-btn analytics-btn-ghost"
              disabled={!!busy || !visible.length}
              onClick={() => assignVisible(bulkStatus)}
            >
              Asignar
            </button>
            <button
              type="button"
              className="analytics-btn analytics-btn-ghost"
              disabled={!!busy || !visible.length}
              onClick={() => assignVisible('')}
            >
              Quitar mapeo
            </button>
          </div>

          <div className="leads-subnav config-tabs" style={{ marginBottom: 12 }}>
            {[
              { id: 'all', label: 'Todas' },
              { id: 'unmapped', label: 'Sin mapear' },
              { id: 'mapped', label: 'Mapeadas (' + mappedCount + ')' },
              ...MEETING_STATUSES.map((s) => ({
                id: s.id,
                label: s.label + ' (' + (counts[s.id] || 0) + ')'
              }))
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                className={'leads-subnav-btn' + (statusFilter === f.id ? ' is-active' : '')}
                onClick={() => setStatusFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {visible.length ? (
            <div className="analytics-panel analytics-panel-scroll" style={{ padding: 0 }}>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Estado de reunión</th>
                    <th>Etapa (Kommo)</th>
                    <th>Embudo (Kommo)</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((it) => (
                    <tr key={stageKey(it)}>
                      <td>
                        <label className="analytics-field" style={{ margin: 0 }}>
                          <span className="visually-hidden">
                            {it.stageName || it.stageId}
                          </span>
                          <select
                            value={it.meetingStatus || ''}
                            disabled={!!busy}
                            onChange={(e) => setStatusFor(it, e.target.value)}
                            aria-label={'Estado reunión: ' + (it.stageName || it.stageId)}
                          >
                            <option value="">— Sin mapear —</option>
                            {MEETING_STATUSES.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </td>
                      <td>
                        <strong>{it.stageName || it.stageId || '—'}</strong>
                        <span className="analytics-sub">
                          {it.meetingStatus
                            ? meetingStatusLabel(it.meetingStatus)
                            : 'Sin mapear'}
                          {' · ID '}
                          {it.stageId || '—'}
                        </span>
                      </td>
                      <td>
                        {it.pipelineName || it.pipelineId || '—'}
                        <span className="analytics-sub">ID {it.pipelineId || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="analytics-empty">
              Sin etapas en este filtro. Sincronizá Kommo (espejo Embudos) y recargá.
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}
